import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, Trash2, Save, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_CARDS = 300;

export default function CreateSetPage() {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [cards, setCards] = useState([
        { id: crypto.randomUUID(), term: '', definition: '' },
        { id: crypto.randomUUID(), term: '', definition: '' }
    ]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const addCard = () => {
        if (cards.length >= MAX_CARDS) return;
        setCards([...cards, { id: crypto.randomUUID(), term: '', definition: '' }]);
    };

    const removeCard = (id) => {
        if (cards.length <= 1) return;
        setCards(cards.filter(c => c.id !== id));
    };

    const updateCard = (id, field, value) => {
        setCards(cards.map(c => 
            c.id === id ? { ...c, [field]: value } : c
        ));
    };

    const handleSave = async () => {
        setError('');
        
        if (!title.trim()) {
            setError('Please enter a title for your set');
            return;
        }

        const validCards = cards.filter(c => c.term.trim() && c.definition.trim());
        if (validCards.length < 2) {
            setError('Please add at least 2 complete flashcards');
            return;
        }

        setSaving(true);
        try {
            const response = await axios.post(`${API}/sets`, {
                title: title.trim(),
                flashcards: validCards.map(c => ({
                    term: c.term.trim(),
                    definition: c.definition.trim()
                }))
            });
            navigate(`/edit/${response.data.id}`);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create set');
        } finally {
            setSaving(false);
        }
    };

    const isAtLimit = cards.length >= MAX_CARDS;
    const validCardCount = cards.filter(c => c.term.trim() && c.definition.trim()).length;

    return (
        <div className="min-h-screen bg-background">
            <Header />

            {/* Sticky Top Bar */}
            <div className="fixed top-16 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border">
                <div className="max-w-4xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <Button
                            variant="ghost"
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-button"
                            className="rounded-none"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Back
                        </Button>

                        <div className="flex items-center gap-4">
                            <span 
                                data-testid="card-counter"
                                className={`font-mono text-xs tracking-wider ${isAtLimit ? 'text-destructive' : 'text-muted-foreground'}`}
                            >
                                {cards.length}/{MAX_CARDS}
                            </span>
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                data-testid="save-set-button"
                                className="rounded-none btn-lift"
                            >
                                <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                {saving ? 'Saving...' : 'Save Set'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <main className="pt-36 pb-16 px-6">
                <div className="max-w-4xl mx-auto">
                    {/* Title Input */}
                    <div className="mb-12">
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter set title..."
                            data-testid="set-title-input"
                            className="w-full font-serif text-3xl md:text-4xl tracking-tight bg-transparent border-0 border-b-2 border-border focus:border-primary focus:outline-none pb-4 placeholder:text-muted-foreground/50"
                        />
                        <p className="mt-2 text-sm text-muted-foreground">
                            {validCardCount} valid card{validCardCount !== 1 ? 's' : ''} ready
                        </p>
                    </div>

                    {/* Error */}
                    {error && (
                        <div 
                            data-testid="create-error"
                            className="mb-8 p-4 bg-destructive/10 border border-destructive/30 text-destructive text-sm flex items-center gap-2"
                        >
                            <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
                            {error}
                        </div>
                    )}

                    {/* Cards List */}
                    <div className="space-y-0">
                        {cards.map((card, index) => (
                            <div 
                                key={card.id}
                                data-testid={`card-row-${index}`}
                                className="card-list-row animate-fade-in"
                            >
                                <div className="col-span-1 font-mono text-xs text-muted-foreground">
                                    {index + 1}
                                </div>
                                <div className="card-list-term">
                                    <input
                                        type="text"
                                        value={card.term}
                                        onChange={(e) => updateCard(card.id, 'term', e.target.value)}
                                        placeholder="Term"
                                        data-testid={`card-term-${index}`}
                                        className="w-full input-minimal font-sans"
                                    />
                                </div>
                                <div className="card-list-definition">
                                    <input
                                        type="text"
                                        value={card.definition}
                                        onChange={(e) => updateCard(card.id, 'definition', e.target.value)}
                                        placeholder="Definition"
                                        data-testid={`card-definition-${index}`}
                                        className="w-full input-minimal font-sans text-muted-foreground"
                                    />
                                </div>
                                <div className="card-list-actions">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeCard(card.id)}
                                        disabled={cards.length <= 1}
                                        data-testid={`delete-card-${index}`}
                                        className="text-muted-foreground hover:text-destructive"
                                    >
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Add Card Button */}
                    <div className="mt-8">
                        {isAtLimit ? (
                            <div className="p-4 border border-dashed border-warning/50 bg-warning/5 text-warning text-sm flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
                                Maximum of {MAX_CARDS} cards reached
                            </div>
                        ) : (
                            <Button
                                variant="outline"
                                onClick={addCard}
                                data-testid="add-card-button"
                                className="w-full rounded-none border-dashed h-14 btn-lift"
                            >
                                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Add Card
                            </Button>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
